import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, MapPin, Phone, User, Truck, Wallet, Loader2, Printer, Receipt } from "lucide-react";
import { api, inr } from "../../lib/api";
import { toast } from "sonner";
import CallButton from "../../components/CallButton";
import { openMap } from "../../lib/openMap";
import { useSubmitGuard } from "../../lib/useSubmitGuard";

const TERMINAL = new Set(["DELIVERED", "CANCELLED", "REJECTED"]);

export default function MerchantOrderDetail() {
    const { id } = useParams();
    const nav = useNavigate();
    const [o, setO] = useState(null);
    const [printPreview, setPrintPreview] = useState(null);

    const load = async () => {
        try { const { data } = await api.get(`/merchant/orders/${id}`); setO(data); }
        catch (e) { toast.error(e?.response?.data?.detail || "Could not load order"); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

    // Bug found in production: `disabled={busy}` alone still let a fast
    // double-click fire two PATCH requests (each triggering its own
    // order-status-change push) before React's re-render actually
    // applied the disabled attribute. useSubmitGuard adds a synchronous
    // ref check ahead of that race — see its docstring.
    const [busy, run] = useSubmitGuard();
    const advance = (next) => run(async () => {
        try { await api.patch(`/merchant/orders/${id}/status?status=${next}`); toast.success(`→ ${next.replace("_", " ")}`); load(); }
        catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    });
    const printIt = async (kind) => {
        try { const { data } = await api.post(`/merchant/print/${id}?kind=${kind}`); setPrintPreview(data); }
        catch (e) { toast.error(e?.response?.data?.detail || "Print failed"); }
    };

    if (!o) return <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin" /></div>;
    const t = o.totals || {};
    const adr = o.delivery_address || {};
    const nextActions = Array.isArray(o.next_actions) ? o.next_actions : [];
    const isTerminal = TERMINAL.has(o.status);
    const cdc = !!o.cdc_enabled;

    return (
        <div>
            <button onClick={() => nav(-1)} className="inline-flex items-center gap-1 text-sm font-semibold text-muted-foreground mb-3"><ArrowLeft size={14} /> Back</button>
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h1 className="text-2xl font-bold">{o.order_unique_id}</h1>
                    <div className="text-sm text-muted-foreground mt-1 inline-flex items-center gap-2 flex-wrap">
                        <Badge>{o.status}</Badge>
                        {o.rider_status && <Badge tone="violet" data-testid="merchant-rider-status">Rider · {o.rider_status.replace(/_/g, " ")}</Badge>}
                        {o.rider_name && <span className="text-xs">· {o.rider_name}{o.rider_mobile ? ` (${o.rider_mobile})` : ""}</span>}
                        {o.zone_name && <Badge tone="sky"><MapPin size={10} className="inline mr-1" />{o.zone_name}</Badge>}
                        {cdc && <Badge tone="violet">CDC</Badge>}
                        {cdc && o.cdc_paid_by_rider && <Badge tone="emerald">CDC paid by rider</Badge>}
                        <span>{o.payment_method} · {o.payment_status}</span>
                    </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <button onClick={() => printIt("KOT")} className="border border-border rounded-xl px-3 py-2 text-xs font-bold inline-flex items-center gap-1"><Printer size={12} /> KOT</button>
                    <button onClick={() => printIt("BILL")} className="border border-border rounded-xl px-3 py-2 text-xs font-bold inline-flex items-center gap-1"><Printer size={12} /> Bill</button>
                    {!isTerminal && nextActions.map((act) => {
                        const isCancel = act.slug === "CANCELLED" || act.slug === "REJECTED";
                        return (
                            <button
                                key={act.slug}
                                data-testid={`mord-action-${act.slug}`}
                                disabled={busy}
                                onClick={() => {
                                    if (isCancel && !confirm(`${act.label} this order?`)) return;
                                    advance(act.slug);
                                }}
                                className={`rounded-xl px-4 py-2 text-sm font-bold inline-flex items-center gap-2 disabled:opacity-50 ${isCancel ? "border border-rose-300 text-rose-700 bg-rose-50" : "text-white"}`}
                                style={isCancel ? undefined : { background: act.color }}
                            >
                                {busy ? <Loader2 size={14} className="animate-spin" /> : null}
                                {isCancel ? `✕ ${act.label}` : `→ ${act.label}`}
                            </button>
                        );
                    })}
                </div>
            </div>

            <div className="grid lg:grid-cols-[1fr_360px] gap-4 mt-6">
                <div className="space-y-4">
                    <Section title="Items" testid="order-items">
                        <ul className="divide-y divide-border">
                            {(o.items || []).map((it) => (
                                <li key={it.product_id + (it.variant_id || "")} className="flex items-center gap-3 py-3">
                                    <span className={`food-mark ${it.food_type === "NON_VEG" ? "non-veg" : "veg"}`} />
                                    <div className="flex-1 min-w-0">
                                        <p className="font-semibold text-sm">{it.name}</p>
                                        <p className="text-xs text-muted-foreground">{inr(it.selling_price)} × {it.quantity}</p>
                                    </div>
                                    <div className="text-sm font-bold">{inr((it.selling_price || 0) * (it.quantity || 1))}</div>
                                </li>
                            ))}
                        </ul>
                        <div className="mt-2 text-xs text-muted-foreground">
                            Total quantity: <strong>{(o.items || []).reduce((s, i) => s + (i.quantity || 0), 0)}</strong>
                        </div>
                    </Section>

                    <Section title="Order summary" testid="order-summary" icon={<Receipt size={14} />}>
                        <Row k="Subtotal (items)" v={inr(t.subtotal)} />
                        {t.packaging_fee > 0 && <Row k="Packaging" v={inr(t.packaging_fee)} />}
                        <Row k="Delivery fee" v={t.delivery_fee === 0 ? <em className="not-italic text-emerald-700 font-semibold">FREE</em> : inr(t.delivery_fee)} />
                        {(t.tax || 0) > 0 && <Row k="Item GST" v={inr(t.tax)} />}
                        {(t.discount || 0) > 0 && (
                            <>
                                <Row
                                    k={<span>Discount <span className="text-[10px] font-semibold text-violet-700 bg-violet-100 dark:bg-violet-950/50 dark:text-violet-300 px-1.5 py-0.5 rounded-full ml-1">{t.discount_charged_to === "SHOP" ? "Merchant absorbs" : "Admin absorbs"}</span></span>}
                                    v={<span className="text-emerald-700">- {inr(t.discount)}</span>}
                                />
                            </>
                        )}
                        <div className="border-t border-border my-2" />
                        <Row k={<strong>Grand total (customer pays)</strong>} v={<strong data-testid="grand-total">{inr(t.grand_total)}</strong>} />
                    </Section>

                    {/* iter134c — Explicit payout breakdown. Merchants
                        kept reading "Discount −₹100 → Your payout ₹450"
                        as "the discount reduced my payout" (it didn't —
                        commission is the reason). Break the math out so
                        it's obvious what's actually being deducted.

                        iter134a1 — user-reported (Feb 2026):
                        "Item sales ₹1,200 → Your payout ₹1,600". The
                        merchant's ``menu_price`` (600) was higher than
                        the customer-facing ``selling_price`` (600 shown,
                        but the snapshot payout was computed against a
                        menu_price of ~800). Result: ``merchant_payout``
                        legitimately exceeds ``subtotal`` — the platform
                        absorbs the delta as a "menu-price uplift". The
                        old code clamped ``commission = max(0, subtotal -
                        payout)`` to zero and hid the ₹400 delta, leaving
                        the merchant asking "where does ₹400 come from?".
                        The fix branches on the sign so the visible
                        line-items always reconcile:
                          subtotal + uplift − commission = payout. */}
                    <Section title="Your payout" testid="payout-breakdown" tone="emerald" icon={<Receipt size={14} />}>
                        <Row k="Item sales" v={inr(t.subtotal)} />
                        {(() => {
                            // Signed delta between the customer-facing
                            // subtotal (selling_price × qty) and what
                            // the merchant is actually credited on the
                            // order snapshot (menu_price − commission,
                            // per pricing.py). Two possible signs:
                            //   delta > 0 → payout beats subtotal.
                            //     The merchant's menu_price is higher
                            //     than the customer's selling_price;
                            //     the platform absorbs the difference.
                            //     Surface it as a positive "adjustment"
                            //     so the line-items reconcile.
                            //   delta < 0 → subtotal beats payout.
                            //     Normal case — the delta IS the
                            //     commission the platform charged.
                            const subtotal = Number(t.subtotal || 0);
                            const payout = Number(t.merchant_payout || 0);
                            const delta = payout - subtotal;
                            if (delta > 0.01) {
                                return (
                                    <Row
                                        k={<span>Platform adjustment <span className="text-[10px] text-muted-foreground">(menu-price uplift)</span></span>}
                                        v={<span data-testid="payout-adjustment" className="text-emerald-600 dark:text-emerald-400">+ {inr(delta)}</span>}
                                    />
                                );
                            }
                            const wedge = -delta;
                            if (wedge <= 0.01) return null;
                            // ``wedge`` (subtotal − payout) is the WHOLE
                            // admin-earn gap, not commission alone — it
                            // also contains any Selling-Price-Increment
                            // markup (a separate pricing lever). Split
                            // them apart using the true per-order
                            // commission snapshot when available so a
                            // shop on a genuine 18% commission with an
                            // SP increment doesn't see an inflated
                            // "Commission" figure that's really markup +
                            // commission combined.
                            const trueCommission = t.commission_amount;
                            const commission = trueCommission != null ? Math.min(Number(trueCommission), wedge) : wedge;
                            const spMarkup = wedge - commission;
                            return (
                                <>
                                    {spMarkup > 0.01 && (
                                        <Row
                                            k={<span>Selling price markup <span className="text-[10px] text-muted-foreground">(platform-retained)</span></span>}
                                            v={<span className="text-red-600 dark:text-red-400">- {inr(spMarkup)}</span>}
                                        />
                                    )}
                                    <Row
                                        k={<span>Commission <span className="text-[10px] text-muted-foreground">(platform fee)</span></span>}
                                        v={<span data-testid="payout-commission" className="text-red-600 dark:text-red-400">- {inr(commission)}</span>}
                                    />
                                </>
                            );
                        })()}
                        <div className="border-t border-border my-2" />
                        <Row k={<strong className="text-emerald-700">Your payout</strong>} v={<strong data-testid="merchant-payout" className="text-emerald-700">{inr(t.merchant_payout)}</strong>} />
                        {(t.discount || 0) > 0 && (
                            <p className="text-[11px] text-violet-700 dark:text-violet-300 bg-violet-50 dark:bg-violet-950/40 rounded-lg px-2 py-1.5 mt-2">
                                <strong>Note:</strong> {t.discount_charged_to === "SHOP"
                                    ? <>the {inr(t.discount)} customer discount (coupon) is absorbed by <strong>you</strong> &mdash; it reduces your payout above.</>
                                    : <>the {inr(t.discount)} customer discount (coins / coupon) is absorbed by the platform &mdash; it does NOT reduce your payout.</>}
                            </p>
                        )}
                        {(() => {
                            // iter134a1 — dedicated note for the
                            // menu-price uplift case. Without this,
                            // an ₹400 "Platform adjustment" line
                            // reads as a mystery bonus. Explain it
                            // in one sentence so the merchant can
                            // reconcile against their own price sheet.
                            const uplift = Number(t.merchant_payout || 0) - Number(t.subtotal || 0);
                            if (uplift > 0.01) {
                                return (
                                    <p
                                        data-testid="payout-uplift-note"
                                        className="text-[11px] text-emerald-800 dark:text-emerald-200 bg-emerald-50 dark:bg-emerald-950/40 rounded-lg px-2 py-1.5 mt-2"
                                    >
                                        <strong>Note:</strong> your product&apos;s menu price is higher than the customer-facing selling price. The platform absorbs the {inr(uplift)} difference as a menu-price uplift — it goes to you, not the platform.
                                    </p>
                                );
                            }
                            return null;
                        })()}
                        <p className="text-xs text-muted-foreground mt-2">Snapshot taken at checkout. Final settlement may include refunds or chargebacks.</p>
                    </Section>

                    {cdc ? (
                        <Section title="CDC — Driver pays you" tone="violet" testid="cdc-section" icon={<Truck size={14} />}>
                            <p className="text-sm">When the rider arrives, you&apos;ll <strong>receive {inr(t.merchant_payout)}</strong> from them as payment for this order. The rider will then collect {inr(t.grand_total)} from the customer{o.payment_method !== "COD" ? " (already prepaid online)" : ""}.</p>
                        </Section>
                    ) : o.payment_method === "COD" ? (
                        <Section title="COD — Driver collects from customer" tone="amber" testid="cod-section" icon={<Wallet size={14} />}>
                            <p className="text-sm">Rider will collect <strong>{inr(t.grand_total)}</strong> from the customer at delivery. Your payout settles separately.</p>
                        </Section>
                    ) : null}
                </div>

                <div className="space-y-4">
                    <Section title="Customer" testid="customer-card">
                        <p className="font-semibold text-sm inline-flex items-center gap-2"><User size={14} /> {o.customer_name || "—"}</p>
                        {/* The server sends the customer's number only when the tenant admin allows vendors to see it
                            (Admin → Shop Owners); without it there is no number line and no Call button. */}
                        {o.customer_mobile && (
                            <p data-testid="cust-mobile" className="text-xs text-muted-foreground mt-1 inline-flex items-center gap-2"><Phone size={12} /> {o.customer_mobile}</p>
                        )}
                        <div className="mt-3 text-xs text-muted-foreground">
                            <p className="font-semibold uppercase tracking-wider text-[10px] mb-1">Deliver to</p>
                            <p className="line-clamp-3">{[adr.line1, adr.line2, adr.city, adr.pincode].filter(Boolean).join(", ")}</p>
                        </div>
                        {/* Same Call/Navigate pair the admin order page has (its ContactRow). openMap prefers the
                            saved map pin; a pin-less address falls back to a maps search of the address text. */}
                        {(o.customer_mobile || adr.line1 || (adr.lat && adr.lng)) && (
                            <div className="flex items-center gap-2 mt-3">
                                {o.customer_mobile && (
                                    <CallButton phone={o.customer_mobile} testid="cust-call" className="flex-1 inline-flex items-center justify-center gap-1.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-xl py-2 text-xs font-bold">
                                        <Phone size={13} /> Call
                                    </CallButton>
                                )}
                                {(adr.line1 || (adr.lat && adr.lng)) && (
                                    <button
                                        type="button"
                                        data-testid="cust-navigate"
                                        onClick={() => openMap({
                                            lat: adr.lat, lng: adr.lng,
                                            label: [adr.line1, adr.city, adr.pincode].filter(Boolean).join(", "),
                                        })}
                                        className="flex-1 inline-flex items-center justify-center gap-1.5 bg-sky-50 text-sky-700 border border-sky-200 rounded-xl py-2 text-xs font-bold"
                                    >
                                        <MapPin size={13} /> Navigate
                                    </button>
                                )}
                            </div>
                        )}
                    </Section>

                    <Section title="Timeline" testid="timeline">
                        <ol className="space-y-2">
                            {(o.events || []).map((e, idx) => (
                                <li key={`${e.ts}-${e.status}-${idx}`} className="text-xs flex items-start gap-2">
                                    <span className="w-2 h-2 mt-1.5 rounded-full bg-primary shrink-0" />
                                    <div>
                                        <p className="font-semibold">{e.status}</p>
                                        <p className="text-muted-foreground">{new Date(e.ts).toLocaleString()} · {e.actor}</p>
                                    </div>
                                </li>
                            ))}
                            {(!o.events || o.events.length === 0) && <p className="text-xs text-muted-foreground">No events yet.</p>}
                        </ol>
                    </Section>
                </div>
            </div>

            {printPreview && (
                <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setPrintPreview(null)}>
                    <div className="bg-white text-black rounded-xl p-4 font-mono text-xs whitespace-pre max-w-md w-full" onClick={(e) => e.stopPropagation()}>
                        {printPreview.content}
                        <button onClick={() => setPrintPreview(null)} className="mt-3 w-full bg-foreground text-background rounded-lg py-2 font-bold text-sm">Close</button>
                    </div>
                </div>
            )}
        </div>
    );
}

function Section({ title, children, testid, icon, tone }) {
    const tones = { violet: "bg-violet-50 border-violet-200", amber: "bg-amber-50 border-amber-200", emerald: "bg-emerald-50 border-emerald-200" };
    const cls = tone ? tones[tone] : "bg-card border-border";
    return (
        <div data-testid={testid} className={`border rounded-2xl p-4 ${cls}`}>
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground mb-2 inline-flex items-center gap-1">{icon}{title}</p>
            {children}
        </div>
    );
}
function Row({ k, v }) { return <div className="flex items-center justify-between py-1.5 text-sm"><span className="text-muted-foreground">{k}</span><span className="font-medium">{v}</span></div>; }
function Badge({ children, tone }) {
    const tones = { sky: "bg-sky-100 text-sky-700", violet: "bg-violet-100 text-violet-700", emerald: "bg-emerald-100 text-emerald-700" };
    const cls = tone ? tones[tone] : "bg-amber-100 text-amber-700";
    return <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full ${cls}`}>{children}</span>;
}
